/**
 * notchTradeImplication — plain-English "what does this notch mean for a
 * trader" text, keyed by (model, tag, outcome).
 *
 * Two shapes returned:
 *   action  — 3-5 word verb phrase for the Right-Now band card ("Sell premium 20-45 DTE")
 *   detail  — one-sentence context sentence, safe to render under any Reading
 *
 * Kept in the frontend (not the Lambda) because it's a display concern that
 * evolves independently of the data. The Lambda ships raw tags + outcomes;
 * the site interprets.
 */

import { ScorecardModel, ScorecardOutcome } from './notchesScorecardService';

export interface Implication {
  action: string;    // 3-5 word CTA for the Right-Now band card
  detail: string;    // one-line trade context under any Reading
}

const GENERIC_HIT: Implication = {
  action: 'Model called it',
  detail: 'This is what a hit looks like for this model.',
};
const GENERIC_MISS: Implication = {
  action: 'Model missed',
  detail: 'This is what a miss looks like — kept in the log to show the losing side.',
};

export function implicationFor(
  model: ScorecardModel, tag: string, outcome: ScorecardOutcome,
): Implication {
  // ---- COT ----------------------------------------------------------------
  if (model === 'COT') {
    const isShort = tag.includes('SHORT');
    if (outcome === 'open') {
      return isShort
        ? { action: 'Bearish · 30-45 DTE',
            detail: 'Hedge funds positioned short. Consider put debit spreads or long puts on the ETF proxy in the 30-45 DTE window.' }
        : { action: 'Bullish · 30-45 DTE',
            detail: 'Hedge funds positioned long. Consider put credit spreads or long calls on the ETF proxy in the 30-45 DTE window.' };
    }
    if (outcome === 'hit') {
      return isShort
        ? { action: 'Bearish call worked', detail: 'The ETF proxy moved lower over the 4-week window as the positioning implied.' }
        : { action: 'Bullish call worked', detail: 'The ETF proxy moved higher over the 4-week window as the positioning implied.' };
    }
    if (outcome === 'miss') {
      return isShort
        ? { action: 'Bearish call missed', detail: 'The ETF proxy rose instead of falling over the 4-week window.' }
        : { action: 'Bullish call missed', detail: 'The ETF proxy fell instead of rising over the 4-week window.' };
    }
  }

  // ---- GEX ----------------------------------------------------------------
  if (model === 'GEX') {
    const isNeg = tag.startsWith('NEG_GEX');
    if (outcome === 'open') {
      return isNeg
        ? { action: 'Long-vol · 5-15 DTE',
            detail: 'Dealers are short gamma — expect intraday vol to expand. Favor straddles or strangles in the 5-15 DTE window; avoid iron condors.' }
        : { action: 'Short-vol · 5-15 DTE',
            detail: 'Dealers are long gamma — expect grinding tight ranges. Favor iron condors or credit spreads in the 5-15 DTE window; avoid straddles.' };
    }
    if (outcome === 'hit') {
      return isNeg
        ? { action: 'Vol did expand', detail: 'Realized volatility rose in the 5 days after this reading, as short-gamma implied.' }
        : { action: 'Vol did compress', detail: 'Realized volatility fell in the 5 days after this reading, as long-gamma implied.' };
    }
    if (outcome === 'miss') {
      return { action: 'Vol did not follow',
        detail: 'Realized volatility moved the opposite direction from what the gamma regime implied.' };
    }
  }

  // ---- IV -----------------------------------------------------------------
  if (model === 'IV') {
    const isHigh = tag === 'IVR_HIGH';
    if (outcome === 'open') {
      return isHigh
        ? { action: 'Sell premium · 20-45 DTE',
            detail: 'Options are expensive for this symbol. Favor put credit spreads, iron condors, or covered calls in the 20-45 DTE window.' }
        : { action: 'Buy premium · 30-60 DTE',
            detail: 'Options are cheap for this symbol. Favor long calls, long puts, or long straddles in the 30-60 DTE window if you expect direction.' };
    }
    if (outcome === 'hit') {
      return isHigh
        ? { action: 'IV mean-reverted', detail: 'Elevated IV dropped past 50 within 10 sessions — the model called the reversion.' }
        : { action: 'IV expanded', detail: 'Compressed IV rose past 50 within 10 sessions — the model called the expansion.' };
    }
    if (outcome === 'miss') {
      return isHigh
        ? { action: 'IV stayed elevated', detail: 'IV did not mean-revert within the 10-day window; premium-sellers who entered may still be workable.' }
        : { action: 'IV stayed compressed', detail: 'IV did not expand within the 10-day window; premium-buyers who entered lost theta.' };
    }
  }

  // ---- AVWAP --------------------------------------------------------------
  if (model === 'AVWAP') {
    if (outcome === 'open') {
      return { action: 'Wait for 5d confirm',
        detail: 'Support is being tested right now. Wait for the 5-day close: hold above AVWAP → bullish, break >2% below → bearish.' };
    }
    if (outcome === 'hit') {
      return { action: 'Support held · bullish',
        detail: 'Institutional support held. Bull put spreads or long calls in the 20-45 DTE window were the trade.' };
    }
    if (outcome === 'miss') {
      return { action: 'Support broke · bearish',
        detail: 'Institutional support failed decisively. Bear call spreads or long puts in the 15-30 DTE window were the trade.' };
    }
    if (outcome === 'neutral') {
      return { action: 'Support unclear',
        detail: 'Spot closed within 2% below AVWAP — inconclusive, no directional read.' };
    }
  }

  // Fallback
  if (outcome === 'hit') return GENERIC_HIT;
  if (outcome === 'miss') return GENERIC_MISS;
  return { action: '', detail: '' };
}
